import styled from 'styled-components';
import { BaSeTheme } from '../../theme';
import { ThemeColorValue } from '../../theme/theme-interface';
import { transformLightness } from '../../utils/color-transformation/transform-lightness';
import type {
  CompactFocusRingMode,
  CompactSegmentSize,
  CompactTitleMaxLines,
} from './compact-stepper';

interface CompactStepButtonProps {
  $color: ThemeColorValue;
  $focusRingMode: CompactFocusRingMode;
  $isClickable: boolean;
}

interface CompactSegmentProps {
  $color: ThemeColorValue;
  $size: CompactSegmentSize;
}

interface CompactTitleTextProps {
  $maxLines: CompactTitleMaxLines;
}

const segmentHeights: Record<CompactSegmentSize, number> = {
  small: 4,
  medium: 6,
  large: 10,
};

const CompactStepperContainer = styled.div.attrs({
  className: 'BaSe--compact-stepper',
})`
  display: flex;
  flex-direction: column;
  gap: 1.875rem;
  width: 100%;
`;

const CompactNavigation = styled.nav.attrs({
  className: 'BaSe--compact-stepper-navigation',
})`
  && {
    display: grid;
  }
  gap: 0.25rem;
  width: 100%;
`;

const CompactHeader = styled.div.attrs({
  className: 'BaSe--compact-stepper-header',
})`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`;

const CompactTitle = styled.div.attrs({
  className: 'BaSe--compact-stepper-title',
})`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
  gap: 0.5rem;
  min-width: 0;

  & > .BaSe--tag {
    flex: none;
  }
`;

const CompactTitleText = styled.div.attrs({
  className: 'BaSe--compact-stepper-title-text',
})<CompactTitleTextProps>`
  flex: 0 1 auto;
  min-width: 0;

  & > [class*='BaSe'] {
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ $maxLines }) => $maxLines};
  }
`;

const CompactCounter = styled.div.attrs({
  className: 'BaSe--compact-stepper-counter',
})`
  flex: none;

  & > [class*='BaSe'] {
    white-space: nowrap;
  }
`;

const CompactSteps = styled.ol.attrs({
  className: 'BaSe--compact-stepper-steps',
})`
  display: flex;
  gap: 0.625rem;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  ${BaSeTheme.breakpoints.mobileSmall.mediaQueryCss(`
    gap: 0.5rem;
  `)}
`;

const CompactStep = styled.li.attrs({
  className: 'BaSe--compact-stepper-step',
})`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const CompactStepButton = styled.button.attrs({
  className: 'BaSe--compact-stepper-step-button',
})<CompactStepButtonProps>`
  appearance: none;
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 5px;
  outline: none;
  background: ${BaSeTheme.colors.defaultColors.transparent};
  cursor: ${({ $isClickable, disabled }) => {
    if (disabled) {
      return 'not-allowed';
    }

    if ($isClickable) {
      return 'pointer';
    }

    return 'default';
  }};
  -webkit-tap-highlight-color: transparent;

  ${({ $color, $focusRingMode }) => `
    &:${$focusRingMode === 'all' ? 'focus' : 'focus-visible'} {
      box-shadow: 0 0 0 3px ${transformLightness($color, 0.8)};
    }
  `}
`;

const CompactSegment = styled.span.attrs({
  className: 'BaSe--compact-stepper-segment',
})<CompactSegmentProps>`
  display: block;
  width: 100%;
  height: ${({ $size }) => `${segmentHeights[$size]}px`};
  border-radius: ${({ $size }) => `${segmentHeights[$size] / 2}px`};
  background-color: ${({ $color }) => $color};
  transition: ${BaSeTheme.transitions.allPropertiesContinuousFast};
`;

export default {
  CompactCounter,
  CompactHeader,
  CompactNavigation,
  CompactSegment,
  CompactStep,
  CompactStepButton,
  CompactStepperContainer,
  CompactSteps,
  CompactTitle,
  CompactTitleText,
};
